import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { capThemTokenRepository } from '../../../lib/repositories/cap-them-token.repository';
import { useToast } from '../../../lib/toast';
import type {
  AiTokenGrant,
  AiTokenGrantCreate,
  AiTokenRequest,
  AiTokenRequestApprove,
  AiTokenRequestCreate,
  AiTokenRequestQueue,
  AiTokenRequestStatus,
} from '../types';
import { KHOA_TOKEN_AI, thuLaiToken } from './useHanMucTokenAi';
import { KHOA_TENANT } from './useTenants';

/**
 * Khoá cache của token cấp thêm và yêu cầu (ADR-0091). Đặt dưới `['han-muc-token']` để vô hiệu hoá hạn mức
 * theo gói vẫn quét được cả nhánh này.
 */
export const KHOA_CAP_THEM = {
  capThem: (tenantId: string) => ['han-muc-token', 'cap-them', tenantId] as const,
  yeuCau: (tenantId: string) => ['han-muc-token', 'yeu-cau', tenantId] as const,
  hangCho: ['han-muc-token', 'hang-cho'] as const,
  hangChoTheo: (status: AiTokenRequestStatus | undefined) => ['han-muc-token', 'hang-cho', status ?? 'tat-ca'] as const,
};

/**
 * Sau mọi thao tác: hạn mức (`add_on`, `limit_value`, `blocked`), danh sách cấp thêm, yêu cầu của tenant, hàng
 * chờ và chi tiết tenant (dòng `AI_TOKENS_MONTH` của bảng hạn mức) đều có thể đổi.
 */
function lamMoi(qc: QueryClient, tenantId: string) {
  void qc.invalidateQueries({ queryKey: KHOA_TOKEN_AI.cuaTenant(tenantId) });
  void qc.invalidateQueries({ queryKey: KHOA_CAP_THEM.capThem(tenantId) });
  void qc.invalidateQueries({ queryKey: KHOA_CAP_THEM.yeuCau(tenantId) });
  void qc.invalidateQueries({ queryKey: KHOA_CAP_THEM.hangCho });
  void qc.invalidateQueries({ queryKey: KHOA_TENANT.chiTiet(tenantId) });
}

/** Token cấp thêm của một tenant, mới nhất trước. `tenantId` trống thì không gọi. */
export function useCapThemToken(tenantId: string | undefined) {
  return useQuery<AiTokenGrant[]>({
    queryKey: KHOA_CAP_THEM.capThem(tenantId ?? ''),
    queryFn: () => capThemTokenRepository.capThemCuaTenant(tenantId ?? ''),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
    retry: thuLaiToken,
  });
}

/** Yêu cầu cấp thêm của một tenant, mới nhất trước. */
export function useYeuCauToken(tenantId: string | undefined) {
  return useQuery<AiTokenRequest[]>({
    queryKey: KHOA_CAP_THEM.yeuCau(tenantId ?? ''),
    queryFn: () => capThemTokenRepository.yeuCauCuaTenant(tenantId ?? ''),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
    retry: thuLaiToken,
  });
}

/** Hàng chờ của mọi tenant (Platform Admin). Đọc lại mỗi phút để huy hiệu số chờ không cũ. */
export function useHangChoYeuCauToken(status: AiTokenRequestStatus | undefined, batDau = true) {
  return useQuery<AiTokenRequestQueue>({
    queryKey: KHOA_CAP_THEM.hangChoTheo(status),
    queryFn: () => capThemTokenRepository.hangCho(status),
    enabled: batDau,
    staleTime: 20_000,
    refetchInterval: 60_000,
    retry: thuLaiToken,
  });
}

/**
 * Nạp thêm hoặc thêm gói tạm thời. `khoaLap` (Idempotency-Key) do hộp sinh một lần khi mở, để bấm lại sau lỗi
 * mạng không cấp hai lần. Lỗi không toast: hộp giữ số đã nhập và hiện lỗi dưới ô.
 */
export function useTaoCapThemToken(tenantId: string, tenTenant: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t, locale } = useT();
  return useMutation({
    mutationFn: ({ du, khoaLap }: { du: AiTokenGrantCreate; khoaLap: string }) =>
      capThemTokenRepository.taoCapThem(tenantId, du, khoaLap),
    onSuccess: (g) => {
      lamMoi(qc, tenantId);
      toast.success(
        t(g.kind === 'NAP_THEM' ? 'tokenAi.daNapThem' : 'tokenAi.daThemGoi', {
          so: soVN(g.tokens, locale),
          ten: tenTenant,
        }),
      );
    },
  });
}

export function useGiaHanCapThemToken(tenantId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation({
    mutationFn: ({ grantId, validTo, note }: { grantId: string; validTo: string; note?: string | null }) =>
      capThemTokenRepository.giaHan(tenantId, grantId, { valid_to: validTo, note: note ?? null }),
    onSuccess: () => {
      lamMoi(qc, tenantId);
      toast.success(t('tokenAi.daGiaHan'));
    },
  });
}

export function useThuHoiCapThemToken(tenantId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation({
    mutationFn: (grantId: string) => capThemTokenRepository.thuHoi(tenantId, grantId),
    onSuccess: () => {
      lamMoi(qc, tenantId);
      toast.success(t('tokenAi.daThuHoi'));
    },
  });
}

/** Tenant Admin gửi yêu cầu. Lỗi (kể cả quá 3 yêu cầu đang chờ) hiện trong hộp, nguyên câu của server. */
export function useGuiYeuCauToken(tenantId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation({
    mutationFn: ({ du, khoaLap }: { du: AiTokenRequestCreate; khoaLap: string }) =>
      capThemTokenRepository.guiYeuCau(tenantId, du, khoaLap),
    onSuccess: () => {
      lamMoi(qc, tenantId);
      toast.success(t('tokenAi.daGuiYeuCau'));
    },
  });
}

export function useHuyYeuCauToken(tenantId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation({
    mutationFn: (requestId: string) => capThemTokenRepository.huyYeuCau(tenantId, requestId),
    onSuccess: () => {
      lamMoi(qc, tenantId);
      toast.success(t('tokenAi.daHuyYeuCau'));
    },
  });
}

export function useDuyetYeuCauToken() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation({
    mutationFn: ({ yeuCau, du }: { yeuCau: AiTokenRequest; du: AiTokenRequestApprove }) =>
      capThemTokenRepository.duyet(yeuCau.id, du),
    onSuccess: (r) => {
      lamMoi(qc, r.tenant_id);
      toast.success(t('tokenAi.daDuyet', { ten: r.tenant_name }));
    },
  });
}

export function useTuChoiYeuCauToken() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation({
    mutationFn: ({ yeuCau, note }: { yeuCau: AiTokenRequest; note: string }) =>
      capThemTokenRepository.tuChoi(yeuCau.id, note),
    onSuccess: (r) => {
      lamMoi(qc, r.tenant_id);
      toast.success(t('tokenAi.daTuChoi', { ten: r.tenant_name }));
    },
  });
}
